import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { RoomsOfferPrice } from 'components/Accommodation/Rooms/SplitBookingCard/RoomsOfferPrice';
import { RoomSwitchRequiredTooltip } from 'components/Offer/Primary/RoomSwitchRequiredTooltip/RoomSwitchRequiredTooltip';
import { useGetRoomOfferAnalyticsContexts } from 'hooks/useGetRoomOfferAnalyticsContexts';
import { getMeta } from 'modules/meta/selectors';
import { getAnchorPriceNightlyPerRoom, getShowTotalPrices } from 'modules/sapiSearch/selectors';
import { getAnchorPriceTotal } from 'modules/sapiSearch/utils';
import { getFilteredRoomsData, getIsRoomsPollingWaitingForResults, getIsRoomsQueryLoading } from 'modules/searchApi/selectors';
import { toggle } from 'opticks';
import { getIsFromGha, getIsFromGVR } from 'utils/trafficSource';
import { Box } from '@daedalus/atlas/helpers/Box';
import { useBrand } from '@daedalus/core/src/_web/brand/hooks/useBrand';
import { useInView } from '@daedalus/core/src/_web/utils/browser/hooks/useInView';
import { useDispatchTeamEvent } from '@daedalus/core/src/analytics/hooks/useDispatchTeamEvent';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, Category, Entity, Page } from '@daedalus/core/src/analytics/types/Events';
import { getShouldSeeOffersUnlocked } from '@daedalus/core/src/auth/modules/selectors';
import { hasOfferTag } from '@daedalus/core/src/offer/business/offers';
import { Offer, OfferTagLabel } from '@daedalus/core/src/offer/types/offer';
import { getDisplayPrice, getTotalPrice } from '@daedalus/core/src/price/business/price';
import { getSplitBookingDates } from '@daedalus/core/src/room/business/utils/getSplitBookingDates';
import { getSplitBookingRooms } from '@daedalus/core/src/room/business/utils/getSplitBookingRooms';
import { sortRoomsAndSplitBooking } from '@daedalus/core/src/room/business/utils/sortRoomsAndSplitBooking';
import { SapiAbstractOffer, SapiAbstractOfferType, SplitBooking } from '@daedalus/core/src/room/types/room';
import { useGetHotelQuery } from '@daedalus/core/src/sapi/services/searchApi';
import { useVioTag } from '@daedalus/core/src/tag';
import { Cashback } from '@daedalus/shared/src/cashback/components/Cashback';
import { useCachedSearchTopOffers } from '../useCachedSearchTopOffers';
import { PrimaryRegularOfferPrice } from './PrimaryRegularOfferPrice';
import { PrimaryRoomOfferCTA } from './PrimaryRoomOfferCTA';
import { PrimaryRoomOfferLabels } from './PrimaryRoomOfferLabels';
import { PrimaryRoomOfferLoading } from './PrimaryRoomOfferLoading';
import { PrimaryRoomOfferLogo } from './PrimaryRoomOfferLogo';
import { PrimaryRoomOfferTag } from './PrimaryRoomOfferTag';
const RoomOfferWrapper = /*#__PURE__*/styled('div')({
  name: "RoomOfferWrapper",
  class: "r6g4zx4",
  propsAsIs: false
});
interface Props {
  hotelId: string;
  component: string;
  shouldCloseOverlayOnClick?: boolean;
  showCashback?: boolean;
}
const getClickedOfferFromEntity = (entity: SapiAbstractOffer) => {
  return entity?.type === SapiAbstractOfferType.SplitBooking ? entity?.data : entity?.data?.offers?.find(offer => offer.isClicked);
};
const getCheapestOfferFromEntity = (entity: SapiAbstractOffer) => {
  return entity?.type === SapiAbstractOfferType.SplitBooking ? entity?.data : entity?.data?.offers?.[0];
};
export const PrimaryRoomOffer = ({
  hotelId,
  component,
  shouldCloseOverlayOnClick,
  showCashback = false
}: Props) => {
  const dispatchTeamEvent = useDispatchTeamEvent();
  const {
    inView,
    ref
  } = useInView({
    triggerOnce: true,
    threshold: 0.5
  });
  const shouldSeeOffersUnlocked = useSelector(getShouldSeeOffersUnlocked);
  const {
    rooms,
    splitBooking
  } = useSelector(getFilteredRoomsData);
  const isLoading = useSelector(getIsRoomsQueryLoading);
  // Added as part of: 1c2b1n6h-ap-rooms-long-polling
  const isRoomsPollingWaitingForResults = useSelector(getIsRoomsPollingWaitingForResults);
  const entities = sortRoomsAndSplitBooking(rooms, splitBooking);
  const clickedEntity = entities.find(entity => entity.type === SapiAbstractOfferType.Regular ? entity.data.hasClickedOffer : entity.data.isClicked);
  const cheapestEntity = entities[0];

  // prioritising clicked over cheapest entity
  const pickedEntity = clickedEntity || cheapestEntity;
  const isClicked = Boolean(clickedEntity);
  const pickedOffer = isClicked ? getClickedOfferFromEntity(pickedEntity) : getCheapestOfferFromEntity(pickedEntity);
  const isYourChoice = hasOfferTag(pickedOffer, OfferTagLabel.IsYourChoice);
  const isSelected = toggle('d8f5ddb4-your-selection-label-ap-and-srp', isClicked, isClicked || isYourChoice);
  const isSplitBookingPicked = pickedEntity?.type === SapiAbstractOfferType.SplitBooking;
  const {
    data: hotel
  } = useGetHotelQuery({
    hotelId
  }, {
    skip: !hotelId
  });
  const analyticsContext = useGetRoomOfferAnalyticsContexts(isSplitBookingPicked ? {
    id: (pickedOffer as SplitBooking)?.bundleId,
    isSplitBooking: true
  } : {
    id: (pickedOffer as Offer)?.id
  });
  const payload = {
    isOfferLocked: false,
    shouldSeeOffersUnlocked
  };
  const {
    currencyCode
  } = useSelector(getMeta);
  const displayPrice = getDisplayPrice(pickedOffer?.prices || []);
  const offerTotalPrice = getTotalPrice(displayPrice);
  const {
    offerEntity
  } = useCachedSearchTopOffers({
    hotelId
  });
  const anchorPrice = useSelector(state => getAnchorPriceNightlyPerRoom(state, offerEntity));
  const anchorPriceTotal = getAnchorPriceTotal(offerEntity);
  const showTotalPrices = useSelector(getShowTotalPrices);
  const {
    nightlyDisplayTotal
  } = displayPrice?.chargeable || {};
  const {
    brandCode
  } = useBrand();
  const vioTagDetails = useVioTag({
    price: showTotalPrices ? offerTotalPrice : nightlyDisplayTotal,
    anchorPrice: showTotalPrices ? anchorPriceTotal : anchorPrice,
    currencyCode,
    brandCode
  });
  const isFromGha = useSelector(getIsFromGha);
  const isFromGVR = useSelector(getIsFromGVR);
  const shouldShowFreeCancellationDate = toggle('feature-free-cancellation-date-gha-gvr-preferred-rate-offers', false, isClicked && (isFromGha || isFromGVR));
  const trackOfferClicked = () => {
    dispatchTeamEvent(trackEvent({
      category: Category.User,
      entity: Entity.Offer,
      action: Action.Clicked,
      page: Page.Accommodation,
      component,
      analyticsContext,
      payload
    }));
  };
  useEffect(() => {
    if (inView) dispatchTeamEvent(trackEvent({
      category: Category.User,
      entity: Entity.Offer,
      action: Action.Viewed,
      page: Page.Accommodation,
      component,
      analyticsContext,
      payload
    }));
  }, [inView]);
  if (isLoading || isRoomsPollingWaitingForResults) {
    return <PrimaryRoomOfferLoading />;
  }
  if (!pickedOffer) return null;
  return <div ref={ref}>
      <Box paddingBottom="s200">
        <PrimaryRoomOfferTag isSelected={isSelected} vioTagDetails={vioTagDetails} />
      </Box>

      <RoomOfferWrapper>
        <div>
          <Box display="flex" flexDirection="column" gap="s200" alignItems="flex-start">
            <PrimaryRoomOfferLogo />
            <PrimaryRoomOfferLabels shouldShowFreeCancellationDate={shouldShowFreeCancellationDate} offers={isSplitBookingPicked ? [pickedEntity.data.offers[0].offer, pickedEntity.data.offers[1].offer] : [pickedOffer as Offer]} />
          </Box>
          {isSplitBookingPicked ? <RoomSwitchRequiredTooltip /> : null}
        </div>
        <Box display="flex" flexDirection="column" alignItems="flex-end" justifyContent="flex-end">
          {isSplitBookingPicked ? <RoomsOfferPrice hotelId={hotelId} prices={pickedOffer.prices} splitBookingDates={getSplitBookingDates(pickedEntity.data)} splitBookingRooms={getSplitBookingRooms(pickedEntity.data)} propertyCurrencyCode={hotel?.currency} /> : <PrimaryRegularOfferPrice hotelId={hotelId} room={pickedEntity.data} offer={pickedOffer as Offer} component={component} isVioTagVisible={vioTagDetails.isVioTagVisible} />}

          {showCashback && !isSplitBookingPicked && <Box marginTop="s200">
              <Cashback value={(pickedOffer as Offer).cashback?.value} type={(pickedOffer as Offer).cashback?.type} />
            </Box>}

          <PrimaryRoomOfferCTA trackOfferClicked={trackOfferClicked} shouldCloseOverlayOnClick={shouldCloseOverlayOnClick} />
        </Box>
      </RoomOfferWrapper>
    </div>;
};

require("./PrimaryRoomOffer.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./PrimaryRoomOffer.tsx");